import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import User from '../../../api/models/user';
import { selectUser } from '../../user/user-slice';
import { createAppAsyncThunk } from '../../../app/thunkWithTypes';
import { RootState } from '../../../app/store';
import { getEnterpriseValueToEbitda, getPriceToEarnings } from '../../../api/client';
import { MultipleValue, ValuationMultiple } from '../../../api/models/valuation/valuationMultiple';
import { awaitWithDiscreteDelay, ValueFormat } from '../../../app/utils';

// Allowed periods
export type Period = '1A' | '5A' | '10A' | 'T';

// All categories
export type MarketDataCategory = typeof marketDataCategories[number];
const marketDataCategories = ['Valuation', 'Rentabilidade'] as const;
// Categories that lack development
export type MarketDataCategoriesComingSoon = typeof marketDataCategoriesComingSoon[number];
const marketDataCategoriesComingSoon = ['Rentabilidade'] as const;

// All market datas
export type MarketData = typeof marketDatas[number];
const marketDatas = [
  'Earnings Yield', 'P/L', 'EV/EBITDA',
  'Margem Bruta', 'Margem EBITDA', 'Margem Líquida', 'ROE'
] as const;
// Market datas that lack development
export type MarketDataComingSoon = typeof marketDataComingSoon[number];
const marketDataComingSoon = [
  'Margem Bruta', 'Margem EBITDA', 'Margem Líquida', 'ROE'
] as const;

// Mapping of value formattings by market data
const valueFormatByMarketData = new Map<MarketData, ValueFormat>([
  ['Earnings Yield', 'percentage'],
  ['P/L', 'multipleTimes'],
  ['EV/EBITDA', 'multipleTimes'],
  ['Margem Bruta', 'percentage'],
  ['Margem EBITDA', 'percentage'],
  ['Margem Líquida', 'percentage'],
  ['ROE', 'percentage'],
])

// Mapping of market datas by category
const marketDataByCategory = new Map<MarketDataCategory, MarketData[]>([
  ['Valuation', ['Earnings Yield', 'P/L', 'EV/EBITDA']],
  ['Rentabilidade', ['Margem Bruta', 'Margem EBITDA', 'Margem Líquida', 'ROE']]
])

interface StockDataState {
  status: Partial<Record<MarketData, 'idle' | 'pending' | 'succeeded' | 'failed'>>
  symbol: string | null
  period: Period
  currentValues: Partial<Record<MarketData, number>>
  chosenCategory: MarketDataCategory
  chosenMarketData: MarketData
  chosenSeries: object
  chosenMin: object
  chosenAvg: number | null
  chosenMax: object
}

const initialState: StockDataState = {
  status: {},
  symbol: null,
  period: '5A',
  currentValues: {},
  chosenCategory: 'Valuation',
  chosenMarketData: 'Earnings Yield',
  chosenSeries: {},
  chosenMin: {},
  chosenAvg: null,
  chosenMax: {},
}

/**
 * Inverts each data value, so x becomes 1/x
 */
function invertValuationMultiple(valuationMultiple: ValuationMultiple | undefined, type: ValuationMultiple['name']): ValuationMultiple {
  const inverted: ValuationMultiple = new ValuationMultiple(type);
  valuationMultiple?.series.forEach(multiple => {
    const newNumerator = multiple.denominator;
    const newDenominator = multiple.numerator;
    const newValue = 1.0 / multiple.value;
    inverted.series.push(new MultipleValue(
      multiple.date,
      newValue,
      newNumerator,
      newDenominator,
      multiple.corporateActionsAdjustment,
      multiple.description
    ));
  });
  return inverted;
}

/**
 * Limits the quantity of points
 * @param valuationMultiple The valuation multiple object containing the series data points.
 * @param maxPoints The maximum number of points allowed.
 * @returns The valuation multiple with its series capped.
 */
function limitSeriesPoints(valuationMultiple: ValuationMultiple | undefined, maxPoints: number = 500): ValuationMultiple | undefined {
  if (valuationMultiple === undefined || valuationMultiple?.series.length <= maxPoints) {
    return valuationMultiple;
  }
  const limited: ValuationMultiple = new ValuationMultiple(valuationMultiple?.name);
  const originalPoints = valuationMultiple?.series.length ?? 0;
  const gap = originalPoints / maxPoints;
  let count = 0;
  valuationMultiple?.series.forEach((multiple, index) => {
    const currentIndex = Math.trunc(index / gap);
    if (currentIndex > count || index === 0 || index === valuationMultiple?.series.length - 1 || multiple.description) {
      limited.series.push(multiple);
      count = currentIndex;
    }
  });
  return limited;
}

/**
 * Calculates the start date of the period given its end date.
 * @param endDate The end date of the period.
 * @param period The period type.
 * @returns The start date of the period.
 */
function calcStartDate(endDate: Date, period: Period): Date {
  const years =
    (period === '1A') ? 1
      : (period === '5A') ? 5
        : (period === '10A') ? 10
          : undefined;

  const date =
    (years === undefined) ? new Date(Date.UTC(2010, 0, 1))
      : new Date(Date.UTC(endDate.getUTCFullYear() - years, endDate.getUTCMonth(), endDate.getUTCDate()));

  return date;
}

export const fetchAllSeries = createAppAsyncThunk('stockData/fetchAllSeries', async (data: { accessToken: string, minTimeMs?: number }, { getState, dispatch }) => {
  if (!data.minTimeMs) {
    data.minTimeMs = 1500;
  }
  const period: Period = '1A';
  // Fetch all market data series, except for the ones still not developed and the selected one (which will be fetched with a differente period)
  for (const marketData of marketDatas.filter(marketData =>
    marketData !== getState().stockData.chosenMarketData
    && !marketDataComingSoon.some(comingSoon => marketData === comingSoon)
  )) {
    dispatch(fetchSeries({
      accessToken: data.accessToken,
      marketData,
      period,
      minTimeMs: data.minTimeMs
    }))
  }
  // Fetch the chosen market data series with the chosen period
  dispatch(fetchSeries({
    accessToken: data.accessToken,
    marketData: getState().stockData.chosenMarketData,
    period: getState().stockData.period,
    minTimeMs: data.minTimeMs
  }))
}, {
  condition(arg, thunkApi) {
    const status = selectStatus(thunkApi.getState())
    const statusKeys = Object.keys(status) as MarketData[];
    if (statusKeys.some(p => status[p] === 'pending')) {
      return false;
    }
  }
})

/**
 * Fetches the data series of the given market data.
 */
export const fetchSeries = createAppAsyncThunk('stockData/fetchSeries', async (data: { accessToken: string, marketData: MarketData, period: Period, minTimeMs?: number }, { getState }) => {
  if (!data.minTimeMs) {
    data.minTimeMs = 1500;
  }
  const symbol = selectSymbol(getState()) ?? '';
  const endDate = new Date();
  const startDate = calcStartDate(endDate, data.period);
  const user = plainToInstance(User, selectUser(getState()));
  const marketDataToFetch = data.marketData;
  if (user) {
    let valuationMultiple: ValuationMultiple | undefined = new ValuationMultiple(undefined);
    switch (marketDataToFetch) {
      case 'P/L':
        valuationMultiple = await awaitWithDiscreteDelay(getPriceToEarnings(data.accessToken, symbol, startDate, endDate), data.minTimeMs);
        valuationMultiple = limitSeriesPoints(valuationMultiple);
        break;
      case 'Earnings Yield':
        const priceToEarnings = await awaitWithDiscreteDelay(getPriceToEarnings(data.accessToken, symbol, startDate, endDate), data.minTimeMs);
        valuationMultiple = invertValuationMultiple(priceToEarnings, 'earningsYield');
        valuationMultiple = limitSeriesPoints(valuationMultiple);
        break;
      case 'EV/EBITDA':
        valuationMultiple = await awaitWithDiscreteDelay(getEnterpriseValueToEbitda(data.accessToken, symbol, startDate, endDate), data.minTimeMs);
        valuationMultiple = limitSeriesPoints(valuationMultiple);
        break;
      default:
        break;
    }
    return instanceToPlain(valuationMultiple);
  } else {
    console.error('An attempt to retrieve the market data without a user occurred.')
  }
}, {
  condition(arg, thunkApi) {
    const status = selectStatus(thunkApi.getState())
    const chosenMarketData = selectChosenMarketData(thunkApi.getState());
    if (status[chosenMarketData] === 'pending') {
      return false;
    }
  }
})

const stockDataSlice = createSlice({
  name: 'stockData',
  initialState,
  reducers: {
    setSymbol(state, action: PayloadAction<string>) {
      state.symbol = action.payload;
    },
    setPeriod(state, action: PayloadAction<Period>) {
      state.period = action.payload;
    },
    setSelectedCategory(state, action: PayloadAction<{ selected: StockDataState['chosenCategory'] }>) {
      state.chosenCategory = action.payload.selected;
    },
    setSelectedMarketData(state, action: PayloadAction<{ selected: StockDataState['chosenMarketData'] }>) {
      state.chosenMarketData = action.payload.selected;
      state.chosenMin = {};
      state.chosenAvg = null;
      state.chosenMax = {};
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSeries.pending, (state, action) => {
        state.status[action.meta.arg.marketData] = 'pending';
      })
      .addCase(fetchSeries.fulfilled, (state, action) => {
        state.status[action.meta.arg.marketData] = 'succeeded';
        if (!action.payload) {
          state.chosenSeries = initialState.chosenSeries;
          state.chosenMin = initialState.chosenMin;
          state.chosenMax = initialState.chosenMax;
          state.chosenAvg = initialState.chosenAvg;
        } else {
          const valuationMultiple = plainToInstance(ValuationMultiple, action.payload);

          // In case the fetched series is the chosen market data, set its values
          const seriesSortedByValue = [...valuationMultiple.series].sort((a, b) => a.value - b.value);
          if (state.chosenMarketData === action.meta.arg.marketData) {
            state.chosenSeries = instanceToPlain(action.payload);
            if (seriesSortedByValue.length > 0) {
              state.chosenMin = instanceToPlain(seriesSortedByValue[0]);
              state.chosenMax = instanceToPlain(seriesSortedByValue[seriesSortedByValue.length - 1]);
              state.chosenAvg = seriesSortedByValue.reduce((prev, cur) => prev + cur.value, 0) / seriesSortedByValue.length;
            }
          }

          // Set current values
          if (valuationMultiple.series.length > 0) {
            const seriesSortedByDate = [...valuationMultiple.series].sort((a, b) => a.date.valueOf() - b.date.valueOf());
            state.currentValues[action.meta.arg.marketData] = seriesSortedByDate[seriesSortedByDate.length - 1].value;
            if (action.meta.arg.marketData === 'P/L') {
              const earningsYield = invertValuationMultiple(plainToInstance(ValuationMultiple, action.payload), 'earningsYield');
              const earningsYieldSortedByDate = earningsYield.series.sort((a, b) => a.date.valueOf() - b.date.valueOf());
              state.currentValues['Earnings Yield'] = earningsYieldSortedByDate[earningsYieldSortedByDate.length - 1].value;
            } else if (action.meta.arg.marketData === 'Earnings Yield') {
              const earningsYield = invertValuationMultiple(plainToInstance(ValuationMultiple, action.payload), 'priceToEarnings');
              const earningsYieldSortedByDate = earningsYield.series.sort((a, b) => a.date.valueOf() - b.date.valueOf());
              state.currentValues['P/L'] = earningsYieldSortedByDate[earningsYieldSortedByDate.length - 1].value;
            }
          }
        }
      })
      .addCase(fetchSeries.rejected, (state, action) => {
        state.status[action.meta.arg.marketData] = 'failed'
        state.chosenSeries = {};
      })
  }
})

// Export selectors
export const selectSymbol = (state: RootState) => state.stockData.symbol;
export const selectPeriod = (state: RootState) => state.stockData.period;
export const selectChosenCategory = (state: RootState) => state.stockData.chosenCategory;
export const selectChosenMarketData = (state: RootState) => state.stockData.chosenMarketData;
export const selectChosenSeries = (state: RootState) => state.stockData.chosenSeries;
export const selectChosenMin = (state: RootState) => state.stockData.chosenMin;
export const selectChosenAvg = (state: RootState) => state.stockData.chosenAvg;
export const selectChosenMax = (state: RootState) => state.stockData.chosenMax;
export const selectStatus = (state: RootState) => state.stockData.status;
export const selectCategories = (state: RootState) => marketDataCategories;
export const selectCategoriesComingSoon = (state: RootState) => marketDataCategoriesComingSoon;
export const selectMarketData = (state: RootState) => marketDataByCategory.get(state.stockData.chosenCategory) ?? [];
export const selectMarketDataComingSoon = (state: RootState) => marketDataComingSoon;
export const selectFormatMap = (state: RootState) => valueFormatByMarketData;
export const selectCurrentValue = (state: RootState) => state.stockData.currentValues;


// Export the auto-generated action creator with the same name
export const { setSymbol, setPeriod, setSelectedMarketData, setSelectedCategory } = stockDataSlice.actions;

// Export the generated reducer function
export default stockDataSlice.reducer;